import React, { useState } from 'react';
import styled from 'styled-components';
import type { FAQItem } from '~/utils/rentals';

interface FAQProps {
  faqData?: FAQItem[];
}

const FAQ: React.FC<FAQProps> = ({ faqData }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!faqData || faqData.length === 0) {
    return null;
  }

  return (
    <FAQContainer>
      <FAQContent>
        <FAQHeader>
          <h2 className="heading">Got questions?</h2>
          <Subtitle>Frequently asked questions</Subtitle>
        </FAQHeader>
        <FAQList>
          {faqData.map((item, index) => (
            <FAQItemWrapper key={index}>
              <QuestionButton
                onClick={() => {
                  toggleFAQ(index);
                }}
                isOpen={openIndex === index}
              >
                {item.question}
                <Arrow isOpen={openIndex === index}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Arrow>
              </QuestionButton>
              <Answer isOpen={openIndex === index}>
                <AnswerContent>
                  <p>{item.answer}</p>
                </AnswerContent>
              </Answer>
            </FAQItemWrapper>
          ))}
        </FAQList>
      </FAQContent>
    </FAQContainer>
  );
};

const FAQContainer = styled.div`
  width: 100%;
  margin: 0px 0 40px;
  padding: 2em 0;
  @media screen and (max-width: 500px) {
    margin: 0;
  }
`;

const FAQContent = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (min-width: 780px) {
    align-items: flex-start;
  }
`;

const FAQHeader = styled.div`
  flex: 0 0 auto;

  h2.heading {
    font-weight: 700;
    color: #1a1a1a;
    font-size: 2em;
    @media (min-width: 780px) {
      margin-bottom: 8px;
    }
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
  font-weight: 400;
`;

const FAQList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

const FAQItemWrapper = styled.div`
  background-color: white;
  border-top: 1px solid #e9ecef;

  &:first-child {
    border-top: none;
  }

  @media (min-width: 780px) {
    margin: 0 -16px;
  }
`;

const QuestionButton = styled.button<{ isOpen: boolean }>`
  width: 100%;
  padding: 24px 8px;
  text-align: left;
  background: none;
  border: none;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  @media (min-width: 780px) {
    font-size: 18px;
    padding: 28px 16px;
  }
`;

const Arrow = styled.div<{ isOpen: boolean }>`
  color: #666;
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
  margin-left: 16px;
`;

const Answer = styled.div<{ isOpen: boolean }>`
  max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};

  p {
    padding: 0 16px 24px;
    margin: 0;
    font-size: 15px;
    line-height: 1.6;
    color: #555;

    @media (min-width: 780px) {
      padding: 0 16px 24px;
      font-size: 16px;
    }
  }
`;

const AnswerContent = styled.div`
  /* Content is always in DOM for SEO */
`;

export default FAQ;
